<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import ToggleSwitch from 'primevue/toggleswitch';
import MultiSelect from 'primevue/multiselect';
import Tag from 'primevue/tag';
import BaseIcon from '@/components/base/BaseIcon.vue';
import { EXPOSED_DECISION_RISK_LEVEL_SLUGS } from '@/types/risk-profile';
import type { DecisionLogic, DecisionResult } from '@/types/risk-profile';

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    decisionLogic?: DecisionLogic | null;
    decisionVersion?: number | null;
    relationshipTypeOptions?: { value: string; label: string }[];
    relationshipTypeIds?: string[];
    description?: string;
  }>(),
  {
    description:
      'Ao gerar o relatório, um parecer será aberto e avaliado pela lógica de decisão '
      + 'configurada nesta política, resultando em aprovação, recusa ou análise manual.',
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'update:relationshipTypeIds', value: string[]): void;
}>();

// The parecer needs at least one relationship type: the policy's, or the account's when the
// policy defines none. No options means the account has none at all.
const unavailable = computed(
  () => props.relationshipTypeOptions !== undefined && props.relationshipTypeOptions.length === 0,
);

// If the options become empty while opted in, drop the opt-in.
watch(unavailable, (value) => {
  if (value && props.modelValue) emit('update:modelValue', false);
}, { immediate: true });

const toggle = () => {
  if (unavailable.value) return;
  emit('update:modelValue', !props.modelValue);
};

// Never let the selection fall back to empty: the last remaining type stays selected, so the
// opt-in can't reach an invalid state once it is on.
const updateRelationshipTypeIds = (value: string[]) => {
  if (!value.length) return;
  emit('update:relationshipTypeIds', value);
};

const showRules = ref(false);

const RISK_LEVEL_LABELS: Record<string, string> = {
  low: 'Baixo',
  medium: 'Médio',
  high: 'Alto',
  very_high: 'Muito Alto',
};

const RISK_LEVEL_SEVERITY: Record<string, string> = {
  low: 'success',
  medium: 'warn',
  high: 'danger',
  very_high: 'danger',
};

const RESULT_META: Record<DecisionResult, { label: string; icon: string; soft: string }> = {
  approve: { label: 'Aprovar', icon: 'check_circle', soft: 'bg-green-100 text-green-700' },
  decline: { label: 'Recusar', icon: 'cancel', soft: 'bg-red-100 text-red-700' },
  manual: { label: 'Manual', icon: 'pan_tool', soft: 'bg-gray-100 text-gray-600' },
};

// Risk level -> result for every exposed level. Any level not listed in the logic is
// implicitly manual (matches the backend rule).
const rows = computed(() => {
  const bySlug = new Map((props.decisionLogic?.rules ?? []).map(r => [r.risk_level_slug, r.result]));
  return EXPOSED_DECISION_RISK_LEVEL_SLUGS.map(slug => ({
    slug,
    label: RISK_LEVEL_LABELS[slug] ?? slug,
    severity: RISK_LEVEL_SEVERITY[slug] ?? 'info',
    result: (bySlug.get(slug) ?? 'manual') as DecisionResult,
  }));
});
</script>

<template>
  <!-- Auto decision cannot be offered for this policy at all: a distinct, locked card replaces
       the opt-in rather than a toggle the user could never switch on. -->
  <div
    v-if="unavailable"
    class="rounded-xl border border-dashed border-slate-300 bg-slate-100/60 px-4 py-4"
  >
    <div class="flex items-start gap-3">
      <div class="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-200/70 text-slate-400">
        <BaseIcon>auto_awesome</BaseIcon>
        <span class="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400">
          <BaseIcon size="xs">lock</BaseIcon>
        </span>
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex items-start justify-between gap-3">
          <p class="min-w-0 text-sm font-semibold text-surface-600">
            Decisão automática indisponível
          </p>
          <span class="shrink-0 rounded-md bg-slate-200/80 px-2 py-0.5 text-xs font-medium text-slate-600">
            Indisponível
          </span>
        </div>
        <p class="mt-1 text-xs leading-5 text-surface-500">
          Cadastre ao menos um tipo de relacionamento na conta para usar esta opção.
        </p>
      </div>
    </div>
  </div>

  <div
    v-else
    class="rounded-xl border px-4 py-4 transition-colors duration-300"
    :class="modelValue ? 'border-blue-300 bg-blue-50/70' : 'border-slate-200 bg-slate-50'"
  >
    <div class="flex items-start gap-3">
      <div
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg transition-colors duration-300"
        :class="modelValue ? 'bg-blue-100 text-blue-600' : 'bg-slate-200/70 text-slate-400'"
      >
        <BaseIcon class="motion-reduce:animate-none" :class="{ 'animate-icon-activate': modelValue }">auto_awesome</BaseIcon>
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <p
              class="text-sm font-semibold cursor-pointer transition-colors"
              :class="modelValue ? 'text-surface-950' : 'text-surface-700'"
              @click="toggle"
            >
              Abrir parecer com decisão automática
            </p>
            <p class="mt-1 text-xs leading-5 text-surface-600">
              {{ description }}
            </p>
          </div>
          <ToggleSwitch
            class="shrink-0"
            :model-value="modelValue"
            @update:model-value="emit('update:modelValue', $event)"
          />
        </div>

        <!-- Relationship types the resulting parecer should cover (only while opted in). -->
        <div v-if="modelValue && relationshipTypeOptions?.length" class="mt-3">
          <label class="mb-1 block text-xs font-medium text-surface-600">
            Tipos de relacionamento do parecer <span class="text-red-500">*</span>
          </label>
          <MultiSelect
            :model-value="relationshipTypeIds ?? []"
            @update:model-value="updateRelationshipTypeIds"
            :options="relationshipTypeOptions"
            option-label="label"
            option-value="value"
            placeholder="Selecionar tipos de relacionamento"
            display="chip"
            class="w-full"
            size="small"
          />
        </div>

        <!-- Inline, expandable read-only view of the configured rules. -->
        <div v-if="decisionLogic" class="mt-3">
          <button
            type="button"
            class="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
            @click="showRules = !showRules"
          >
            <BaseIcon size="xs">{{ showRules ? 'expand_less' : 'expand_more' }}</BaseIcon>
            <span>{{ showRules ? 'Ocultar regras de decisão' : 'Ver regras de decisão' }}</span>
            <span v-if="decisionVersion != null" class="whitespace-nowrap text-slate-400">· v{{ decisionVersion }}</span>
          </button>

          <div v-if="showRules" class="mt-2 overflow-hidden rounded-lg border border-blue-200 bg-white">
            <div
              v-for="row in rows"
              :key="row.slug"
              class="flex items-center justify-between gap-4 border-b border-blue-100 px-3 py-2 last:border-b-0"
            >
              <Tag :value="row.label" :severity="row.severity" />
              <span
                class="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium"
                :class="RESULT_META[row.result].soft"
              >
                <BaseIcon size="xs">{{ RESULT_META[row.result].icon }}</BaseIcon>
                {{ RESULT_META[row.result].label }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
